import * as Y from 'yjs';
import YProvider from 'y-partyserver/provider';
import type { Awareness } from 'y-protocols/awareness';
import { LOCAL_ORIGIN, SCHEMA_VERSION } from '@os/doc';
import { nextResetAt, RESOURCE_ROLES, type ResourceRole, type RoomMode } from '@os/shared';
import { jitteredDelay } from './backoff';
import { createIdlePolicy, type IdlePolicyOptions } from './idle';
import { parseMode, resumeAt } from './mode';

export type SyncStatus = 'connecting' | 'online' | 'offline' | 'revoked';

export type SyncProvider = {
  readonly status: SyncStatus;
  /** Role announced by the room on connect and on every change; null until the first one. */
  readonly role: ResourceRole | null;
  /** Local edits not yet confirmed by a full sync with the room. */
  readonly pendingCount: number;
  /** True once the current connection completed a full sync with the room. */
  readonly synced: boolean;
  /** The room says when edits are written (P16-3, `saved` messages); false for an older room. */
  readonly acks: boolean;
  /**
   * The document schema the room announced (P14), null until it does (or for an older room). Above this build's
   * SCHEMA_VERSION the tab is outdated: it must not write (the update banner already offers a reload).
   */
  readonly roomSchema: number | null;
  /** This tab's own edits not yet written by the room (its clock is ahead of the last `saved` vector). */
  readonly ownUnsaved: boolean;
  /** When the room last wrote everything this tab had sent. */
  readonly savedAt: number | null;
  /** When the room last wrote more of this tab's edits (its entry in a `saved` vector grew), even if not all. */
  readonly progressAt: number | null;
  /** The room closed the connection for too many messages (4429); cleared by the next full sync. */
  readonly failure: 'rate_limited' | null;
  /**
   * The room's quota mode (P12, spec §18.4): `normal` on every new socket until the room says otherwise; `suspended`
   * after a 4503 close, until the next socket opens. Edits made meanwhile stay on this device and merge then.
   */
  readonly mode: RoomMode;
  /** When the room's quota resets (next 00:00 UTC, ISO), as the room said; null until it says a mode. */
  readonly resetAt: string | null;
  /** Reconnects now (the save indicator's "Retry"). */
  retry(): void;
  readonly awareness: Awareness;
  /** Tells the room's other connections that a comment thread changed (spec §7.4 #7); dropped while offline. */
  notifyComments(threadId: string): void;
  /** Threads changed by someone else on this board. */
  onComments(cb: (threadId: string) => void): () => void;
  subscribe(cb: () => void): () => void;
  destroy(): void;
};

export type SyncProviderOptions = {
  boardId: string;
  doc: Y.Doc;
  /** A fresh 60 s room ticket (POST /api/rooms/:id/ticket); called before every connection. */
  getTicket: () => Promise<string>;
  /** Defaults to the page's host (the Worker serves the SPA and /sync on the same origin). */
  host?: string;
  WebSocketImpl?: typeof WebSocket;
  /** Voluntary disconnect of an unattended tab; tests pass a no-op. */
  idlePolicy?: (h: Pick<IdlePolicyOptions, 'onIdle' | 'onActive'>) => { destroy(): void };
};

/** Room close codes (BoardRoom): access removed, too many messages, or the day's quota spent. */
const CLOSE_REVOKED = 4403;
const CLOSE_RATE_LIMITED = 4429;
const CLOSE_QUOTA = 4503;

/** This client's clock in a room's `saved` state vector (base64), or null when it is not one. */
function savedClockOf(sv: string, clientId: number): number | null {
  try {
    const bytes = Uint8Array.from(atob(sv), (c) => c.charCodeAt(0));
    return Y.decodeStateVector(bytes).get(clientId) ?? 0;
  } catch {
    return null;
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Pending edits per board, kept across reloads: edits made offline, then a closed tab and a role lowered
 * meanwhile, must still raise the rejected-edits alert when the board is reopened (Review Focus 3).
 */
const pendingKey = (boardId: string) => `os:pending:${boardId}`;
const readPending = (boardId: string): number => {
  try {
    return Number(localStorage.getItem(pendingKey(boardId))) || 0;
  } catch {
    return 0;
  }
};
const writePending = (boardId: string, n: number): void => {
  try {
    if (n > 0) localStorage.setItem(pendingKey(boardId), String(n));
    else localStorage.removeItem(pendingKey(boardId));
  } catch {
    // storage unavailable (private mode): the count only lives in memory
  }
};

/**
 * The board's live connection (spec §7.4-§7.5): y-partyserver provider with a fresh ticket on every (re)connection,
 * jittered backoff up to 30 s, revocation without reconnecting, and an idle disconnect.
 */
export function createSyncProvider(opts: SyncProviderOptions): SyncProvider {
  const host = opts.host ?? location.host;
  const secure = opts.host ? !/^(localhost|127\.0\.0\.1)(:|$)/.test(host) : location.protocol === 'https:';
  const subs = new Set<() => void>();
  const notify = () => subs.forEach((cb) => cb());
  const commentSubs = new Set<(threadId: string) => void>();

  let status: SyncStatus = 'connecting';
  let role: ResourceRole | null = null;
  let pendingCount = readPending(opts.boardId);
  let attempt = 0;
  let idle = false;
  let destroyed = false;
  let acks = false;
  let roomSchema: number | null = null;
  let savedClock = 0;
  let savedAt: number | null = null;
  let progressAt: number | null = null;
  let failure: 'rate_limited' | null = null;
  let mode: RoomMode = 'normal';
  let resetAt: string | null = null;
  /** After a 4503: no connection before this time (ms), whatever asks for one (backoff, Retry, idle wake). */
  let suspendedUntil = 0;
  const setMode = (next: RoomMode) => {
    if (next === mode) return;
    mode = next;
    notify();
  };
  const ownClock = () => Y.getState(opts.doc.store, opts.doc.clientID);
  /** Notifies only when "my edits are all written" flips (never at every keystroke). */
  let unsaved = false;
  const checkUnsaved = () => {
    const next = ownClock() > savedClock;
    if (next === unsaved) return;
    unsaved = next;
    notify();
  };
  const set = (next: SyncStatus) => {
    if (status === 'revoked' || status === next) return;
    status = next;
    notify();
  };

  const ws = new YProvider(host, opts.boardId, opts.doc, {
    prefix: `/sync/${opts.boardId}`,
    protocol: secure ? 'wss' : 'ws',
    // The provider's own retry delay stays short: the jittered 1 s → 30 s backoff in params() is the real one.
    maxBackoffTime: 1_000,
    WebSocketPolyfill: opts.WebSocketImpl,
    disableBc: true,
    connect: false,
    // Retries with backoff until a ticket comes (a rejection on the first connect would stop the provider for
    // good); rejects only once the board is gone for this user (not_found → revoked) or the provider is destroyed.
    params: async () => {
      for (;;) {
        if (attempt++ > 0) await sleep(jitteredDelay(attempt - 2));
        while (suspendedUntil > Date.now()) await sleep(suspendedUntil - Date.now());
        if (destroyed || status === 'revoked') throw new Error('sync stopped');
        try {
          // `schema`: the room keeps a tab of another build read-only (P14 Ruling 7).
          return { ticket: await opts.getTicket(), schema: String(SCHEMA_VERSION) };
        } catch (err) {
          if ((err as { code?: unknown }).code === 'not_found') revoke();
        }
      }
    },
  });
  // A stopped provider rejects connect(); the reason is already in `status`.
  const connect = () => void ws.connect().catch(() => undefined);
  const revoke = () => {
    if (status === 'revoked') return;
    status = 'revoked';
    ws.disconnect();
    notify();
  };

  ws.on('status', ({ status: s }: { status: 'connected' | 'disconnected' | 'connecting' }) => {
    // The backoff resets on a full sync, not on open: a room that accepts then fails (e.g. it cannot load its
    // document) must not be retried several times a second.
    if (s === 'connected') {
      setMode('normal'); // the room says a mode only when it is not normal
      set('online');
    } else set(s === 'connecting' && status !== 'offline' ? 'connecting' : 'offline');
  });
  ws.on('connection-close', (event: CloseEvent | null) => {
    if (event?.code === CLOSE_REVOKED) return revoke();
    if (event?.code === CLOSE_QUOTA) {
      resetAt ??= nextResetAt(new Date()).toISOString();
      suspendedUntil = resumeAt(resetAt, Date.now());
      setMode('suspended');
    }
    if (event?.code === CLOSE_RATE_LIMITED && !failure) {
      failure = 'rate_limited';
      notify();
    }
    set('offline');
  });
  ws.on('synced', (synced: boolean) => {
    if (synced) {
      attempt = 0;
      failure = null;
      pendingCount = 0;
      writePending(opts.boardId, 0);
    }
    notify();
  });
  ws.on('custom-message', (raw: string) => {
    let msg: { type?: unknown; role?: unknown; threadId?: unknown; acks?: unknown; sv?: unknown; schema?: unknown; mode?: unknown; resetAt?: unknown };
    try {
      msg = JSON.parse(raw) as typeof msg;
    } catch {
      return;
    }
    if (msg?.type === 'comments:changed' && typeof msg.threadId === 'string' && UUID.test(msg.threadId)) {
      const id = msg.threadId;
      commentSubs.forEach((cb) => cb(id));
      return;
    }
    const quota = msg ? parseMode(msg) : null;
    if (quota) {
      resetAt = quota.resetAt ?? nextResetAt(new Date()).toISOString();
      setMode(quota.mode);
      return;
    }
    if (msg?.type === 'saved' && typeof msg.sv === 'string') {
      const clock = savedClockOf(msg.sv, opts.doc.clientID);
      if (clock === null) return;
      if (clock > savedClock) progressAt = Date.now();
      savedClock = Math.max(savedClock, clock);
      if (ownClock() <= savedClock) savedAt = Date.now();
      checkUnsaved();
      return;
    }
    if (msg?.type === 'role' && msg.acks === 1 && !acks) {
      acks = true;
      notify();
    }
    if (msg?.type === 'role' && Number.isInteger(msg.schema) && msg.schema !== roomSchema) {
      roomSchema = msg.schema as number;
      notify();
    }
    if (msg?.type === 'role' && RESOURCE_ROLES.includes(msg.role as ResourceRole) && msg.role !== role) {
      role = msg.role as ResourceRole;
      notify();
    }
  });

  const onTransaction = (tr: Y.Transaction) => {
    if (tr.origin !== LOCAL_ORIGIN || tr.changed.size === 0) return;
    checkUnsaved();
    if (ws.wsconnected && ws.synced) return;
    pendingCount++;
    writePending(opts.boardId, pendingCount);
    notify();
  };
  opts.doc.on('afterTransaction', onTransaction);

  const policy = (opts.idlePolicy ?? createIdlePolicy)({
    onIdle: () => {
      idle = true;
      ws.disconnect();
      set('offline');
    },
    onActive: () => {
      if (!idle || status === 'revoked') return;
      idle = false;
      attempt = 0;
      connect();
    },
  });

  connect();

  return {
    get status() {
      return status;
    },
    get role() {
      return role;
    },
    get pendingCount() {
      return pendingCount;
    },
    get synced() {
      return ws.synced;
    },
    get acks() {
      return acks;
    },
    get roomSchema() {
      return roomSchema;
    },
    get ownUnsaved() {
      return unsaved;
    },
    get savedAt() {
      return savedAt;
    },
    get progressAt() {
      return progressAt;
    },
    get failure() {
      return failure;
    },
    get mode() {
      return mode;
    },
    get resetAt() {
      return resetAt;
    },
    retry() {
      if (destroyed || status === 'revoked') return;
      ws.disconnect();
      attempt = 0;
      connect();
    },
    awareness: ws.awareness,
    notifyComments(threadId) {
      if (ws.wsconnected) ws.sendMessage(JSON.stringify({ type: 'comments:changed', threadId }));
    },
    onComments(cb) {
      commentSubs.add(cb);
      return () => void commentSubs.delete(cb);
    },
    subscribe(cb) {
      subs.add(cb);
      return () => void subs.delete(cb);
    },
    destroy() {
      destroyed = true;
      policy.destroy();
      opts.doc.off('afterTransaction', onTransaction);
      ws.destroy();
      subs.clear();
      commentSubs.clear();
    },
  };
}
